import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouterState,
  HeadContent,
} from "@tanstack/react-router";
import { useState, useEffect, type FC } from "react";
import { Slack, Send, Menu, X, LogOut, MessageSquare } from "lucide-react";
import { Toaster } from "@donex/ui";
import { ConfirmProvider } from "@/lib/confirm";
import { AppProvider } from "@/contexts/app-context";
import { AuthProvider, useAuth } from "@/contexts/auth-context";
import { FeedbackModal } from "@/components/FeedbackModal";
import { useInstallations } from "@/stores/installations";
import AuthPage from "@/pages/Auth";
import OnboardingPage from "@/pages/Onboarding";
import logo from "@/assets/donex-logo.svg";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={reset}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "done[x]" },
      { name: "description", content: "Act where you talk." },
      { property: "og:title", content: "done[x]" },
      {
        property: "og:description",
        content: "Act where you talk",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "done[x]" },
      {
        name: "twitter:description",
        content: "Act where you talk.",
      },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function NavLink({
  to,
  Icon,
  label,
  exact,
  onClick,
}: {
  to: string;
  Icon: FC<{ className?: string }>;
  label: string;
  exact?: boolean;
  onClick?: () => void;
}) {
  const { location } = useRouterState();
  const isActive = exact ? location.pathname === to : location.pathname.startsWith(to);
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex flex-col items-center gap-1 rounded-xl px-2 py-3 text-[10px] font-medium transition-all cursor-pointer ${
        isActive
          ? "bg-primary/15 text-primary"
          : "text-muted-foreground hover:bg-elevated hover:text-foreground"
      }`}
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </Link>
  );
}

function AppShell({
  mobileNavOpen,
  setMobileNavOpen,
}: {
  mobileNavOpen: boolean;
  setMobileNavOpen: (v: boolean) => void;
}) {
  const { signOut } = useAuth();
  const closeMenu = () => setMobileNavOpen(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <>
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[480px] w-[480px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-1/3 -right-40 h-[420px] w-[420px] rounded-full bg-accent/5 blur-3xl" />
      </div>
      <header className="relative shrink-0 border-b border-border/60 bg-background backdrop-blur-xl">
        <div className="flex items-center justify-between px-6 py-6">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Done[x]" className="h-[1.3rem] w-auto" />
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
              Beta
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <MessageSquare className="h-4 w-4" />
              Feedback
            </button>
            <button
              type="button"
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="text-muted-foreground transition-colors hover:text-foreground sm:hidden"
            >
              {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <div className="relative flex flex-1 overflow-hidden">
        {mobileNavOpen && (
          <div
            className="absolute inset-0 z-30 bg-background/80 backdrop-blur-sm sm:hidden"
            onClick={closeMenu}
          />
        )}
        <div
          className={`absolute inset-y-0 left-0 z-40 flex flex-col gap-1 border-r border-border/60 bg-background px-2 py-4 transition-transform duration-200 sm:hidden ${
            mobileNavOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <NavLink to="/installations" Icon={Slack} label="Installs" onClick={closeMenu} />
          {/* <NavLink to="/actions" Icon={Zap} label="Actions" onClick={closeMenu} /> */}
          <NavLink to="/" Icon={Send} label="Send" exact onClick={closeMenu} />
          <button
            type="button"
            onClick={() => {
              closeMenu();
              setFeedbackOpen(true);
            }}
            className="mt-auto flex flex-col items-center gap-1 rounded-xl px-2 py-3 text-[10px] font-medium text-muted-foreground transition-all hover:bg-elevated hover:text-foreground"
          >
            <MessageSquare className="h-5 w-5" />
            <span>Feedback</span>
          </button>
          <button
            type="button"
            onClick={() => signOut()}
            className="flex flex-col items-center gap-1 rounded-xl px-2 py-3 text-[10px] font-medium text-muted-foreground transition-all hover:bg-elevated hover:text-foreground"
          >
            <LogOut className="h-5 w-5" />
            <span>Sign out</span>
          </button>
        </div>
        <nav className="hidden shrink-0 flex-col gap-1 border-r border-border/60 bg-background px-2 py-4 sm:flex">
          <NavLink to="/installations" Icon={Slack} label="Installs" />
          {/* <NavLink to="/actions" Icon={Zap} label="Actions" /> */}
          <NavLink to="/" Icon={Send} label="Send" exact />
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="mt-auto flex flex-col items-center gap-1 rounded-xl px-2 py-3 text-[10px] font-medium text-muted-foreground transition-all hover:bg-elevated hover:text-foreground"
          >
            <MessageSquare className="h-5 w-5" />
            <span>Feedback</span>
          </button>
          <button
            type="button"
            onClick={() => signOut()}
            className="flex flex-col items-center gap-1 rounded-xl px-2 py-3 text-[10px] font-medium text-muted-foreground transition-all hover:bg-elevated hover:text-foreground"
          >
            <LogOut className="h-5 w-5" />
            <span>Sign out</span>
          </button>
        </nav>
        <main className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-10">
          <Outlet />
        </main>
      </div>
    </>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <>
      <HeadContent />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppProvider>
            <ConfirmProvider>
              <Toaster theme="dark" />
              <AuthGate mobileNavOpen={mobileNavOpen} setMobileNavOpen={setMobileNavOpen} />
            </ConfirmProvider>
          </AppProvider>
        </AuthProvider>
      </QueryClientProvider>
    </>
  );
}

function AuthGate({
  mobileNavOpen,
  setMobileNavOpen,
}: {
  mobileNavOpen: boolean;
  setMobileNavOpen: (v: boolean) => void;
}) {
  const { user, loading } = useAuth();
  const fetchInstallations = useInstallations((s) => s.fetch);
  const installations = useInstallations((s) => s.installations);
  const installationsInitialized = useInstallations((s) => s.initialized);

  useEffect(() => {
    if (user) fetchInstallations(user.id);
  }, [fetchInstallations, user]);

  if (loading || (user && !installationsInitialized)) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  if (installations.length === 0) {
    return <OnboardingPage />;
  }

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <AppShell mobileNavOpen={mobileNavOpen} setMobileNavOpen={setMobileNavOpen} />
    </div>
  );
}
